import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bot, Loader2, Play, Users, FileText, Layers, Code2, Crown, ChevronRight,
  Eye, FolderOpen, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { formatApiDetail, parseResponseJson } from '../lib/http';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const AGENT_ICONS = {
  team_leader: Crown,
  pm: FileText,
  architect: Layers,
  engineer: Code2,
};

const EVENT_STYLES = {
  status: 'text-sky-300',
  agent_start: 'text-violet-300',
  message: 'text-zinc-400',
  artifact: 'text-emerald-300',
  agent_done: 'text-zinc-500',
  code_start: 'text-amber-300',
  files: 'text-cyan-300',
  preview: 'text-emerald-400',
  done: 'text-emerald-400',
  error: 'text-red-400',
};

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export default function MetaGPT({ user }) {
  const [idea, setIdea] = useState('Create a landing page for a small coffee shop');
  const [statusInfo, setStatusInfo] = useState(null);
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [events, setEvents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activeAgent, setActiveAgent] = useState(null);
  const [artifacts, setArtifacts] = useState({});
  const [files, setFiles] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [artifactTab, setArtifactTab] = useState('feed');
  const esRef = useRef(null);
  const feedRef = useRef(null);
  const previewBlobRef = useRef('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/metagpt/status`, { headers: authHeaders() });
      if (res.ok) {
        const { data } = await parseResponseJson(res);
        setStatusInfo(data);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const loadRunArtifacts = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/metagpt/projects/${id}/status`, { headers: authHeaders() });
      if (!res.ok) return;
      const { data } = await parseResponseJson(res);
      if (data.files) setFiles(data.files);
      if (data.preview_html) {
        setPreviewHtml(data.preview_html);
      }
      if (data.file_paths?.length && !selectedFile) {
        setSelectedFile(data.file_paths[0]);
      }
    } catch {
      /* ignore */
    }
  }, [selectedFile]);

  useEffect(() => {
    loadStatus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = '';
      }
    };
  }, [loadStatus]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [events]);

  const subscribe = useCallback((id) => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    const token = localStorage.getItem('token') || '';
    const url = `${API_URL}/api/metagpt/projects/${id}/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === 'end') {
          es.close();
          esRef.current = null;
          setRunning(false);
          loadRunArtifacts(id);
          return;
        }
        setEvents((prev) => [...prev, payload]);

        if (payload.type === 'roster' && payload.agents) {
          setAgents(payload.agents);
        }
        if (payload.type === 'agent_start') {
          setActiveAgent(payload.agent_id);
        }
        if (payload.type === 'artifact') {
          setArtifacts((prev) => ({ ...prev, [payload.agent_id]: payload.content }));
        }
        if (payload.type === 'preview' && payload.preview_html) {
          setPreviewHtml(payload.preview_html);
        }
        if (payload.type === 'files' && payload.paths) {
          loadRunArtifacts(id);
        }
        if (payload.type === 'done') {
          setRunning(false);
          loadRunArtifacts(id);
          toast.success('Landing page generated — see Preview tab');
        }
        if (payload.type === 'error') {
          setRunning(false);
          toast.error(payload.message || 'Run failed');
        }
      } catch {
        /* ignore */
      }
    };
  }, [loadRunArtifacts]);

  const startRun = async () => {
    if (!idea.trim()) {
      toast.error('Enter a one-line requirement');
      return;
    }
    setRunning(true);
    setEvents([]);
    setAgents([]);
    setArtifacts({});
    setFiles({});
    setPreviewHtml('');
    setSelectedFile(null);
    setActiveAgent(null);
    setArtifactTab('feed');
    try {
      const res = await fetch(`${API_URL}/api/metagpt/projects`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ idea: idea.trim(), project_name: 'MetaGPT Project' }),
      });
      const { data } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, 'Failed to start'));
      setRunId(data.run_id);
      subscribe(data.run_id);
    } catch (err) {
      setRunning(false);
      toast.error(err.message || 'Failed to start MetaGPT');
    }
  };

  const openPreviewTab = () => {
    if (!previewHtml) return;
    if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
    const blob = new Blob([previewHtml], { type: 'text/html;charset=utf-8' });
    previewBlobRef.current = URL.createObjectURL(blob);
    window.open(previewBlobRef.current, '_blank', 'noopener,noreferrer');
  };

  const filePaths = Object.keys(files).sort();
  const headerExtras = (
    <Button
      type="button"
      size="sm"
      disabled={running}
      onClick={startRun}
      className="h-8 bg-violet-600 text-xs hover:bg-violet-500"
      data-testid="metagpt-run-button"
    >
      {running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
      Start Software Company
    </Button>
  );

  const agentStatus = (agentId) => {
    if (artifacts[agentId]) return 'done';
    if (activeAgent === agentId) return 'active';
    const idx = agents.findIndex((a) => a.id === agentId);
    const activeIdx = agents.findIndex((a) => a.id === activeAgent);
    if (activeIdx >= 0 && idx < activeIdx) return 'done';
    return 'idle';
  };

  return (
    <DashboardShell
      user={user}
      title="MetaGPT"
      subtitle="PM → Architect → Engineer → Coder — plans, files, and live preview"
      activeNav="metagpt"
      headerExtras={headerExtras}
      fullscreen
      backPath="/dashboard"
    >
      <div className="flex h-full min-h-0 gap-3 p-3" data-testid="metagpt-page">
        {/* Left: prompt + agents */}
        <div className="flex w-[280px] shrink-0 flex-col gap-2">
          <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.06] p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-violet-200">
              <Bot className="h-3.5 w-3.5" />
              Requirement
            </div>
            <Textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              rows={3}
              disabled={running}
              className="min-h-[72px] resize-none border-white/10 bg-black/40 text-xs text-zinc-100"
              placeholder='e.g. "Landing page for a coffee shop"'
              data-testid="metagpt-idea-input"
            />
          </div>

          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-2.5">
            <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <Users className="h-3 w-3" />
              Agents
            </div>
            <div className="space-y-1.5">
              {(agents.length ? agents : [
                { id: 'team_leader', name: 'Alex', title: 'Team Leader', color: '#a78bfa' },
                { id: 'pm', name: 'Alice', title: 'PM', color: '#60a5fa' },
                { id: 'architect', name: 'Bob', title: 'Architect', color: '#34d399' },
                { id: 'engineer', name: 'Charlie', title: 'Engineer', color: '#fbbf24' },
              ]).map((agent) => {
                const Icon = AGENT_ICONS[agent.id] || Bot;
                const st = agentStatus(agent.id);
                return (
                  <div
                    key={agent.id}
                    className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                      st === 'active' ? 'border-violet-500/40 bg-violet-500/10' : 'border-white/[0.06]'
                    }`}
                  >
                    <Icon className="h-3 w-3 shrink-0" style={{ color: agent.color }} />
                    <span className="truncate text-[10px] text-zinc-300">{agent.title}</span>
                    {st === 'active' && <Loader2 className="ml-auto h-3 w-3 animate-spin text-violet-400" />}
                    {st === 'done' && <ChevronRight className="ml-auto h-3 w-3 text-emerald-400" />}
                  </div>
                );
              })}
              <div className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${running && !previewHtml ? 'border-amber-500/30 bg-amber-500/10' : previewHtml ? 'border-emerald-500/20' : 'border-white/[0.06]'}`}>
                <Code2 className="h-3 w-3 text-amber-400" />
                <span className="text-[10px] text-zinc-300">Coder</span>
                {running && !previewHtml && <Loader2 className="ml-auto h-3 w-3 animate-spin text-amber-400" />}
                {previewHtml && <ChevronRight className="ml-auto h-3 w-3 text-emerald-400" />}
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-white/[0.08] bg-[#0a0a0a]">
            <div className="flex gap-1 border-b border-white/[0.06] px-2 py-1.5">
              {['feed', ...Object.keys(artifacts)].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setArtifactTab(tab)}
                  className={`rounded px-2 py-0.5 text-[10px] capitalize ${artifactTab === tab ? 'bg-violet-500/20 text-violet-200' : 'text-zinc-500'}`}
                >
                  {tab === 'feed' ? 'Log' : tab.replace('_', ' ')}
                </button>
              ))}
            </div>
            <div ref={feedRef} className="min-h-0 flex-1 overflow-auto p-2 font-mono text-[10px] leading-4 text-zinc-500">
              {artifactTab === 'feed' ? (
                events.map((ev, i) => (
                  <div key={i} className="mb-1">
                    <span className={EVENT_STYLES[ev.type] || 'text-zinc-600'}>[{ev.type}]</span>{' '}
                    <span className="text-zinc-500">{ev.message || (typeof ev.content === 'string' ? ev.content.slice(0, 80) : '')}</span>
                  </div>
                ))
              ) : (
                <pre className="whitespace-pre-wrap text-zinc-300">{artifacts[artifactTab] || ''}</pre>
              )}
            </div>
          </div>
        </div>

        {/* Center: preview */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col rounded-xl border border-white/[0.08] bg-[#0a0a0a]">
          <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Eye className="h-3.5 w-3.5" />
              Preview
              {running && !previewHtml ? (
                <span className="text-amber-400">Generating…</span>
              ) : previewHtml ? (
                <span className="text-emerald-400">{filePaths.length} files</span>
              ) : null}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!previewHtml}
              onClick={openPreviewTab}
              className="h-7 border-white/10 text-[10px]"
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              Open tab
            </Button>
          </div>
          <div className="relative min-h-0 flex-1 bg-white" data-testid="metagpt-preview">
            {previewHtml ? (
              <iframe
                title="MetaGPT preview"
                srcDoc={previewHtml}
                className="h-full w-full border-0"
                sandbox="allow-scripts allow-forms allow-popups"
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-[#111] text-sm text-zinc-500">
                {running ? 'Agents planning, then Coder builds your page…' : 'Run a prompt to generate a live preview'}
              </div>
            )}
          </div>
        </div>

        {/* Right: files */}
        <div className="flex w-[260px] shrink-0 flex-col rounded-xl border border-white/[0.08] bg-[#0a0a0a]">
          <div className="flex items-center gap-2 border-b border-white/[0.06] px-3 py-2 text-xs text-zinc-400">
            <FolderOpen className="h-3.5 w-3.5" />
            Files
          </div>
          <div className="min-h-0 flex-1 overflow-auto" data-testid="metagpt-files">
            {filePaths.length === 0 ? (
              <p className="p-3 text-[10px] text-zinc-600">Files appear after the Coder step.</p>
            ) : (
              filePaths.map((path) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => setSelectedFile(path)}
                  className={`block w-full truncate border-b border-white/[0.04] px-3 py-2 text-left font-mono text-[10px] ${
                    selectedFile === path ? 'bg-violet-500/10 text-violet-200' : 'text-zinc-400 hover:bg-white/[0.03]'
                  }`}
                >
                  {path}
                </button>
              ))
            )}
          </div>
          {selectedFile && files[selectedFile] ? (
            <pre className="max-h-[40%] shrink-0 overflow-auto border-t border-white/[0.06] p-2 font-mono text-[9px] leading-4 text-emerald-200/90">
              {files[selectedFile]}
            </pre>
          ) : null}
        </div>
      </div>
    </DashboardShell>
  );
}
